import {
  Chip,
  Group,
  MultiSelect,
  Popover,
  Text,
  Tooltip,
  Box,
} from "@mantine/core"
import { UseFormReturnType } from "@mantine/form"
import {
  IconBrandGithub,
  IconBuildingSkyscraper,
  IconCloud,
  IconCode,
  IconWand,
} from "@tabler/icons-react"
import { usePostHog } from "posthog-js/react"

import {
  EventsRequests,
  EventsResponses,
  getEventsFromCategory,
} from "@costory/types/endpoints/events"
import { GitHubResponses } from "@costory/types/endpoints/github"
import {
  Filters,
  SearchEvents,
  defaultSearchEvents,
} from "@costory/types/filters"
import { EventCategory } from "@costory/types/prisma-client"

import { Dayjs } from "@costory/shared/dayjs"
import { filterEventsByDateRange } from "@costory/shared/utils/dateRange"

const MAX_REPOS_TO_SHOW = 3
// Main component that exports all the filter chips
export const EventsModalChips = ({
  showSuggestedEvents,
  toggleSuggestedEvents,
  form,
  eventsData,
  githubRepos,
  selectedDateRange,
  isAWSPresent,
}: {
  showSuggestedEvents?: boolean
  toggleSuggestedEvents?: () => void
  form: UseFormReturnType<Filters>
  eventsData?: EventsResponses.Aggregated | undefined
  githubRepos: GitHubResponses.Repos | undefined
  selectedDateRange?: { from: Dayjs; to: Dayjs }
  isAWSPresent?: boolean
}) => {
  const eventsFilter = form.values.eventsFilter || defaultSearchEvents
  // Calculate counts from eventsData
  const getEventCount = (
    category?: EventCategory,
    filterFn?: (event: EventsRequests.Event) => boolean,
    isEnabled?: boolean,
  ) => {
    // Return 0 if the filter is disabled
    if (isEnabled === false) return 0

    if (!eventsData) return 0

    let events = getEventsFromCategory(eventsData, category)
      .map((el) => el.events)
      .flat()

    // Apply date filtering
    events = filterEventsByDateRange(events, selectedDateRange)

    // Apply additional filtering if provided
    if (filterFn) {
      events = events.filter(filterFn)
    }

    return events.length
  }

  const formatCount = (count: number) => {
    return `(${count > 9 ? "9+" : count})`
  }

  const posthog = usePostHog()
  const handleChipChange = (
    category: EventCategory,
    enabled: boolean,
    subCategory?: "commitments" | "marketplacePurchases" | "reservations",
  ) => {
    if (category === EventCategory.PROVIDER && subCategory) {
      posthog.capture("Explorer Event Chip Toggled", {
        eventCategory: category,
        enabled,
        eventSubCategory: subCategory,
      })
    } else {
      posthog.capture("Explorer Event Chip Toggled", {
        eventCategory: category,
        enabled,
      })
    }
  }

  return (
    <Group gap="xs">
      {toggleSuggestedEvents && (
        <>
          <Chip
            checked={showSuggestedEvents}
            onChange={() => {
              toggleSuggestedEvents()
            }}
            data-intercom-target="costory-tour-explorer-events-business-chip"
          >
            <Group gap={4}>
              <IconWand size={14} />
              Suggested
            </Group>
          </Chip>
        </>
      )}
      <Chip
        checked={eventsFilter.customBusinessEvents}
        onChange={(checked) => {
          const newEventsFilter = {
            ...eventsFilter,
            customBusinessEvents: checked,
          }
          form.setFieldValue("eventsFilter", newEventsFilter)
          handleChipChange(EventCategory.BUSINESS, checked)
        }}
        data-intercom-target="costory-tour-explorer-events-business-chip"
      >
        <Group gap={4}>
          <IconBuildingSkyscraper size={14} />
          Business
          <Text size="xs">
            {formatCount(
              getEventCount(
                EventCategory.BUSINESS,
                (e) => e.metadata?.source !== "github",
                eventsFilter.customBusinessEvents,
              ),
            )}
          </Text>
        </Group>
      </Chip>

      {/* Custom Technical Events */}
      <Chip
        checked={eventsFilter.customTechnicalEvents}
        onChange={(checked) => {
          const newEventsFilter = {
            ...eventsFilter,
            customTechnicalEvents: checked,
          }
          form.setFieldValue("eventsFilter", newEventsFilter)
          handleChipChange(EventCategory.TECHNICAL, checked)
        }}
        data-intercom-target="costory-tour-explorer-events-technical-chip"
      >
        <Group gap={4}>
          <IconCode size={14} />
          Technical
          <Text size="xs">
            {formatCount(
              getEventCount(
                EventCategory.TECHNICAL,
                (e) => e.metadata?.source !== "github",
                eventsFilter.customTechnicalEvents,
              ),
            )}
          </Text>
        </Group>
      </Chip>

      {/* Provider Events - Commitments */}
      <Tooltip label="Commitments are currently supported only on AWS">
        <Box>
          <Chip
            checked={eventsFilter.commitments}
            onChange={(checked) => {
              const newEventsFilter = { ...eventsFilter, commitments: checked }
              form.setFieldValue("eventsFilter", newEventsFilter)
              handleChipChange(EventCategory.PROVIDER, checked, "commitments")
            }}
            data-intercom-target="costory-tour-explorer-events-commitments-chip"
            disabled={!isAWSPresent}
          >
            <Group gap={4}>
              <IconCloud size={14} />
              Commitments
              <Text size="xs">
                {formatCount(
                  getEventCount(
                    EventCategory.PROVIDER,
                    (e) =>
                      e.labels.some((l) => l.toLowerCase() === "commitment"),
                    eventsFilter.commitments,
                  ),
                )}
              </Text>
            </Group>
          </Chip>
        </Box>
      </Tooltip>

      {/* Provider Events - Marketplace */}
      <Chip
        checked={eventsFilter.marketplacePurchases}
        onChange={(checked) => {
          const newEventsFilter = {
            ...eventsFilter,
            marketplacePurchases: checked,
          }
          form.setFieldValue("eventsFilter", newEventsFilter)
          handleChipChange(
            EventCategory.PROVIDER,
            checked,
            "marketplacePurchases",
          )
        }}
        data-intercom-target="costory-tour-explorer-events-marketplace-chip"
      >
        <Group gap={4}>
          <IconCloud size={14} />
          Marketplace
          <Text size="xs">
            {formatCount(
              getEventCount(
                EventCategory.PROVIDER,
                (e) => e.labels.some((l) => l.toLowerCase() === "marketplace"),
                eventsFilter.marketplacePurchases,
              ),
            )}
          </Text>
        </Group>
      </Chip>

      {/* Provider Events - Reservations */}
      <Tooltip label="Reservations are currently supported only on AWS">
        <Box>
          <Chip
            checked={eventsFilter.reservations}
            onChange={(checked) => {
              const newEventsFilter = { ...eventsFilter, reservations: checked }
              form.setFieldValue("eventsFilter", newEventsFilter)
              handleChipChange(EventCategory.PROVIDER, checked, "reservations")
            }}
            data-intercom-target="costory-tour-explorer-events-reservations-chip"
            disabled={!isAWSPresent}
          >
            <Group gap={4}>
              <IconCloud size={14} />
              Reservations
              <Text size="xs">
                {formatCount(
                  getEventCount(
                    EventCategory.PROVIDER,
                    (e) =>
                      e.labels.some((l) => l.toLowerCase() === "reservation"),
                    eventsFilter.reservations,
                  ),
                )}
              </Text>
            </Group>
          </Chip>
        </Box>
      </Tooltip>

      {/* GitHub Repository Events */}
      {githubRepos?.availableRepo &&
        githubRepos.availableRepo.length > 0 &&
        githubRepos.availableRepo.slice(0, MAX_REPOS_TO_SHOW).map((repo) => {
          const reposSelected = eventsFilter.githubSourceRepos
          return (
            <Chip
              key={repo.name}
              checked={reposSelected.includes(repo.name)}
              onChange={() => {
                const newRepos = reposSelected.includes(repo.name)
                  ? reposSelected.filter((r: string) => r !== repo.name)
                  : [...reposSelected, repo.name]
                const newEventsFilter = {
                  ...eventsFilter,
                  githubSourceRepos: newRepos,
                }
                form.setFieldValue("eventsFilter", newEventsFilter)
              }}
              data-intercom-target="costory-tour-explorer-events-github-chip"
            >
              <Group gap={4}>
                <IconBrandGithub size={14} />
                {repo.name}
              </Group>
            </Chip>
          )
        })}
      {githubRepos?.availableRepo &&
        githubRepos.availableRepo.length > MAX_REPOS_TO_SHOW && (
          <AdditionalRepos
            form={form}
            githubRepos={githubRepos}
            eventsFilter={eventsFilter}
          />
        )}
    </Group>
  )
}

function AdditionalRepos({
  form,
  githubRepos,
  eventsFilter,
}: {
  form: UseFormReturnType<Filters>
  githubRepos: GitHubResponses.Repos
  eventsFilter: SearchEvents
}) {
  const additionalRepos = githubRepos.availableRepo
    .slice(MAX_REPOS_TO_SHOW)
    .map((repo) => ({ label: repo.name, value: repo.name }))
  const selectedRepos = eventsFilter.githubSourceRepos.filter((r: string) =>
    additionalRepos.map((repo) => repo.value).includes(r),
  )
  return (
    <Popover width={250} position="bottom" withArrow shadow="md" trapFocus>
      <Popover.Target>
        <Chip
          checked={eventsFilter.githubSourceRepos.some((r: string) =>
            additionalRepos.map((r) => r.value).includes(r),
          )}
          data-intercom-target="costory-tour-explorer-events-more-repos-chip"
        >
          <Group gap={4}>
            <IconBrandGithub size={14} />
            {selectedRepos.length > 0
              ? `${selectedRepos.length} ${
                  selectedRepos.length === 1 ? "repository" : "repositories"
                } selected`
              : `${additionalRepos.length} more repositories`}
          </Group>
        </Chip>
      </Popover.Target>
      <Popover.Dropdown>
        <MultiSelect
          data={additionalRepos}
          value={selectedRepos}
          onChange={(selected) => {
            const firstThree = eventsFilter.githubSourceRepos.filter(
              (r: string) => !additionalRepos.map((r) => r.value).includes(r),
            )
            const newRepos = Array.from(new Set([...firstThree, ...selected]))
            const newEventsFilter = {
              ...eventsFilter,
              githubSourceRepos: newRepos,
            }
            form.setFieldValue("eventsFilter", newEventsFilter)
          }}
          placeholder="Select more repositories"
          searchable
          nothingFoundMessage="No repositories"
          size="xs"
          maxDropdownHeight={200}
          data-intercom-target="costory-tour-explorer-events-repos-multiselect"
        />
      </Popover.Dropdown>
    </Popover>
  )
}
